import clsx from 'clsx';

type HeadingProps = {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  icon?: React.ReactNode;
  subTitle?: string;
  variant?: 'default' | 'warning';
} & React.ComponentPropsWithoutRef<'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'>;

export function Heading({
  className,
  icon,
  level = 1,
  subTitle,
  variant = 'default',
  ...props
}: HeadingProps) {
  const Element: `h${typeof level}` = `h${level}`;

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      {!!icon && (
        <div
          className={clsx('rounded-lg flex items-center justify-center size-12 shrink-0', {
            'bg-gradient-to-bl from-primary-100 via-primary-200 to-secondary-200 text-primary-800':
              variant === 'default',
            'bg-gradient-to-bl from-warning-100 via-warning-200 to-warning-300 text-warning-700':
              variant === 'warning',
          })}
        >
          {icon}
        </div>
      )}
      <div className='flex flex-col items-start justify-center'>
        <Element
          {...props}
          className={clsx('text-2xl/8 font-semibold sm:text-xl/8 dark:text-white', {
            'text-primary-600': variant === 'default' && level === 1,
            'text-default-950': variant === 'default' && level !== 1,
            'text-warning-600': variant === 'warning',
          })}
        />
        {!!subTitle && <p className='text-sm text-gray-500'>{subTitle}</p>}
      </div>
    </div>
  );
}

export function Subheading({ className, level = 2, ...props }: HeadingProps) {
  const Element: `h${typeof level}` = `h${level}`;

  return (
    <Element
      {...props}
      className={clsx(
        className,
        'text-base/7 font-semibold text-default-950 sm:text-sm/6 dark:text-white',
      )}
    />
  );
}
